/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Component voor het beheer van de tekstuele modelbeschrijving.
 *
 * Gegenereerd met Junie (AI), 23-03-2026
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 */
import {Component, DestroyRef, effect, ElementRef, inject, OnInit, QueryList, untracked, ViewChild, ViewChildren} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService, TranslatePipe} from '@ngx-translate/core';
import type {Core, NodeSingular} from 'cytoscape';
import {firstValueFrom} from 'rxjs';
import {KeyedTimeout} from 'sihw-ngx';
import {BaseIslandComponent} from '../base-island.component';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import {GlobalModelcanvasService} from '../../app/model/global-modelcanvas.service';
import {DlModel} from '../../app/model/dl-model';
import {OpslagService} from '../../app/model/opslag.service';
import {SimulatieService} from '../../app/simulatie/simulatie.service';
import {WeergaveInstellingenService} from '../../services/weergave-instellingen.service';
import {Dynascape} from '../../lib/dynascape';
import {ValuesliderComponent} from '../../controls/valueslider/valueslider.component';

export type BeschrijvingGroep =
    'entiteiten'
    | 'configuraties'
    | 'grootheden'
    | 'correspondentie_waardenbereik'
    | 'correspondentie_waarden'
    | 'correspondentie_afgeleidebereik'
    | 'correspondentie_afgeleide_waarde'
    | 'calculus'
    | 'grootheid_ongelijkheid'
    | 'afgeleide_ongelijkheid'
    | 'oorzaak_gevolg'
    | 'waarden'
    | 'conditieset';

export interface BeschrijvingFragment {
    tekst: string;
    isGrootheid?: boolean;
    isDType?: boolean;
    isKanAannemen?: boolean;
    params?: any;
}

export interface BeschrijvingGroepData {
    groep?: BeschrijvingGroep;
    regels?: BeschrijvingRegel[];
    condities?: BeschrijvingRegel[]; // Voor conditieset: de cond-zinnen
    consequenties?: BeschrijvingRegel[]; // Voor conditieset: de cons-zinnen
    titel: string; // De naam of vertaalde groepsnaam
    condtag?: string;
}

/**
 * Interface voor een regel in de tekstuele beschrijving.
 */
export interface BeschrijvingRegel {
    id?: string; // unieke id voor tracking
    regel: BeschrijvingFragment[];
    status: 'nieuw' | 'bekend' | 'verwijderd';
    groep: BeschrijvingGroep;
    nodes: string[]; //relevante nodes
    isVet?: boolean; // of de regel vetgedrukt moet zijn (nieuw sinds laatste update)
    sorteerNode?: any; // node die gebruikt wordt voor de sortering van de regels
    positieNummer?: number; // positie binnen de groep voor stabiele weergave
    condtag?: string;
    condstate?: 'cond' | 'cons';
}

/**
 * Component voor het weergeven van de tekstuele beschrijving van het model.
 *
 * Gegenereerd met Junie (AI), 23-03-2026
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 */
@Component({
    //de tagnaam ligt vast: `dl-build.component.scss` stijlt erop (`#tekstbeschrijving-wrapper
    //dl-tekst-beschrijving`)
    selector: 'dl-tekst-beschrijving',
    standalone: true,
    imports: [TranslateModule, TranslatePipe, ValuesliderComponent, FormsModule],
    templateUrl: './tekst-beschrijving.component.html',
    styleUrl: './tekst-beschrijving.component.scss'
})
export class TekstBeschrijvingComponent extends BaseIslandComponent implements OnInit {
    /**
     * De permanente eigenaar van de modelcanvas-`cy`.
     */
    private readonly modelcanvas = inject(GlobalModelcanvasService);

    /**
     * Het document: nieuwe posities van de nodes veranderen de volgorde van de regels.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De opslagketen: zijn meldingen zeggen wanneer de beschrijving bijgewerkt moet worden.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * De simulatie: na een simulatie zijn de regels niet meer nieuw.
     */
    private readonly simulatie = inject(SimulatieService);

    /**
     * Of de beschrijving aan staat; bij het aanzetten moet hij één keer opgebouwd worden.
     */
    private readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /**
     * De standaard fontgrootte.
     */
    protected readonly STANDAARD_FONTGROOTTE = 12;

    /**
     * Tolerantie voor het sorteren op schermlocatie (in pixels).
     */
    private readonly SORTEER_TOLERANTIE_X = 100;
    private readonly SORTEER_TOLERANTIE_Y = 100;

    /**
     * De fontgrootte van de tekst in de beschrijving (via slider).
     */
    protected fontGrootte: number = this.STANDAARD_FONTGROOTTE;

    /**
     * De vertaal-service voor meertaligheid.
     */
    private translate = inject(TranslateService);

    /**
     * De feedbackselectie: de hover op een regel highlight de betrokken nodes op het modelcanvas.
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /**
     * Referentie naar de scrollbare inhoud-container.
     */
    @ViewChild('scrollContainer') private scrollContainer?: ElementRef<HTMLDivElement>;

    /**
     * Referenties naar de individuele regels.
     */
    @ViewChildren('regelElement') private regelElementen?: QueryList<ElementRef<HTMLDivElement>>;

    /**
     * De Dynascape helper voor model-inspectie.
     */
    private dyna?: Dynascape;

    /**
     * De lijst met tegenwoordige groepen beschrijvingen.
     */
    protected groepen: BeschrijvingGroepData[] = [];

    /**
     * De lijst met alle gegenereerde beschrijvingsregels voor tracking en animatie.
     */
    private alleRegels: BeschrijvingRegel[] = [];

    /**
     * De lijst met IDs van regels die vetgedrukt moeten blijven (omdat ze recent zijn toegevoegd).
     */
    private vetgedrukteRegelIds = new Set<string>();

    /**
     * Of de component klaar is met laden van vertalingen.
     */
    public isKlaar: boolean = false;

    /**
     * De update-debounce ('update')
     */
    private readonly timers = new KeyedTimeout();

    /**
     * Teller voor het genereren van unieke IDs voor de regels.
     */
    private static idTeller: number = 0;

    constructor() {
        super("info");
        //Waarop de beschrijving bijwerkt. Afmelden bij het opruimen, want dit element kan opnieuw
        //aangemaakt worden.
        const opruimen = inject(DestroyRef);
        //de regels staan op volgorde van de plek van hun nodes: na een sleep, snapNorm of posities van
        //de leider opnieuw opbouwen
        opruimen.onDestroy(this.dlModel.bijPositiesGewijzigd(() => this.doeUpdate()));
        //een vers model: eerst alles weg, en daarna de eerste vulling zonder vetgedrukte regels
        opruimen.onDestroy(this.opslag.bijGereset(() => this.reset()));
        opruimen.onDestroy(this.opslag.bijGeladen(() => {
            this.reset();
            this.doeUpdate(true);
        }));
        //een wijziging van de inhoud of de eigenschappen verandert wat er staat
        opruimen.onDestroy(this.opslag.bijInhoudGewijzigd(() => this.doeUpdate()));
        opruimen.onDestroy(this.opslag.bijPropsGewijzigd(() => this.doeUpdate()));
        //na een simulatie zijn de regels niet meer nieuw
        opruimen.onDestroy(this.simulatie.bijGesimuleerd(() => this.resetVetgedrukt()));
        //en zodra de beschrijving weer aangezet wordt, staat er nog niets: dan één keer opbouwen
        effect(() => {
            if (this.weergaveInstellingen.tekstbeschrijvingAan()) {
                this.doeUpdate();
            } else {
                untracked(() => this.resetVetgedrukt());
            }
        });
    }

    /**
     * De `cy` van het modelcanvas, of `null` als er (nog) geen is. De template toont pas iets als hij er
     * is; `doeUpdate` draait de change detection die dat opnieuw bekijkt.
     */
    protected get cy(): Core | null {
        return this.modelcanvas.getCy();
    }

    /**
     * Initialisatie van de component.
     */
    public async ngOnInit(): Promise<void> {
        // Lees fontgrootte uit local storage
        const opgeslagenGrootte = localStorage.getItem('dl_tekst_fontgrootte');
        if (opgeslagenGrootte) {
            const grootte = parseInt(opgeslagenGrootte, 10);
            if (!isNaN(grootte) && grootte >= 6 && grootte <= 20) {
                this.fontGrootte = grootte;
            }
        }

        // Wacht tot de vertalingen geladen zijn voordat we de UI tonen
        if (this.translate.currentLang) {
            await firstValueFrom(this.translate.get('TEKSTBESCHRIJVING.TITEL'));
        }
        this.isKlaar = true;
    }

    /**
     * Wordt aangeroepen wanneer de fontgrootte wijzigt via de slider.
     */
    protected onFontGrootteChange(nieuweGrootte: number): void {
        localStorage.setItem('dl_tekst_fontgrootte', nieuweGrootte.toString());
    }

    /**
     * Overschrijft de doeUpdate van de basisklasse om de beschrijving opnieuw te genereren.
     */
    override doeUpdate(isEersteVulling: boolean = false): void {
        this.log.debug('*** doeUpdate()');

        // Voorkomt dat het scherm flikkert bij snelle opeenvolgende wijzigingen (debounce)
        this.timers.set('update', () => {
            this.genereerBeschrijving(isEersteVulling);
            super.doeUpdate();

            // Verplaatst de weergave naar de nieuwste wijziging
            this.scrollToLastNewOrChanged();
        }, 200);
    }

    /**
     * Reset de interne staat van de component, zodat bij de volgende update geen visuele effecten (animaties, vet) optreden.
     */
    public reset(): void {
        this.log.debug('*** reset()');
        this.alleRegels = [];
        this.groepen = [];
        this.dyna = undefined;
        this.vetgedrukteRegelIds.clear();
        this.timers.clear('update');
    }

    /**
     * Reset alleen de vetgedrukte status van de regels.
     * Wordt aangeroepen bij een simulatie of het sluiten van de beschrijving.
     */
    public resetVetgedrukt(): void {
        this.log.debug('*** resetVetgedrukt()');
        this.vetgedrukteRegelIds.clear();
        this.doeUpdate();
    }

    /**
     * Scrolt naar de laatste nieuwe of gewijzigde regel.
     * @private
     */
    private scrollToLastNewOrChanged(): void {
        setTimeout(() => {
            if (!this.alleRegels.length || !this.regelElementen) return;

            // Zoek de laatste index van een regel die 'nieuw' is of 'isVet' (gewijzigd) in de geflatteerde lijst
            let targetIndex = -1;
            for (let i = this.alleRegels.length - 1; i >= 0; i--) {
                const b = this.alleRegels[i];
                if (b.status === 'nieuw' || b.isVet) {
                    targetIndex = i;
                    break;
                }
            }

            // Haal het element op uit de QueryList
            const elRef = this.regelElementen.toArray()[targetIndex];
            if (elRef) {
                elRef.nativeElement.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            }
        }, 150); // Iets langere delay om zeker te zijn dat QueryList is bijgewerkt
    }

    /**
     * Analyseert het model en bouwt de array met beschrijvingen op.
     */
    private genereerBeschrijving(isEersteVulling: boolean= false): void {
        if (!this.cy) {
            this.groepen = [];
            this.alleRegels = [];
            this.dyna = undefined;
            return;
        }

        // Als er nog geen regels zijn, is dit de eerste vulling.
        //const isEersteVulling = this.alleRegels.length === 0;

        this.dyna = new Dynascape(this.cy);

        // Verzamel alle unieke condtags in het model
        const alleCondTags = new Set<string>();
        this.cy.$('[condtag]').forEach(n => {
            const tag = n.data('condtag');
            if (tag) alleCondTags.add(tag);
        });

        // Hulpmethode om alle beschrijvingsmethoden aan te roepen met specifieke conditie-parameters
        const verzamelAlleRegels = (tag: string | null, state?: 'cond' | 'cons' | null): BeschrijvingRegel[] => {
            return [
                ...this.beschrijfLosseEntiteiten(tag, state),
                ...this.beschrijfConfiguraties(tag, state),
                ...this.beschrijfGrootheden(tag, state),
                ...this.beschrijfWaardebereiken(tag, state),
                ...this.beschrijfCorrespondentieWaardenbereik(tag, state),
                ...this.beschrijfCorrespondentieWaarden(tag, state),
                ...this.beschrijfCorrespondentieAfgeleidebereik(tag, state),
                ...this.beschrijfCorrespondentieAfgeleideWaarde(tag, state),
                ...this.beschrijfCalculus(tag, state),
                ...this.beschrijfGrootheidOngelijkheid(tag, state),
                ...this.beschrijfAfgeleideOngelijkheid(tag, state),
                ...this.beschrijfOorzaakGevolg(tag, state)
            ];
        };

        // 1. Verzamel de normale set (zonder conditieset)
        const normaleSet = verzamelAlleRegels(null, null);

        // 2. Verzamel conditiesets
        const conditieSetsData = new Map<string, { cond: BeschrijvingRegel[], cons: BeschrijvingRegel[] }>();
        alleCondTags.forEach(tag => {
            const cond = verzamelAlleRegels(tag, 'cond');
            const cons = verzamelAlleRegels(tag, 'cons');

            this.log.debug(`Conditieset ${tag}: ${cond.length} condities, ${cons.length} consequenties`);
            // Alleen toevoegen als er minstens één consequentie (cons) is
            if (cons.length > 0) {
                conditieSetsData.set(tag, {cond, cons});
            }
        });

        const groepsNamen: BeschrijvingGroep[] = ['entiteiten', 'configuraties', 'grootheden', 'oorzaak_gevolg', 'waarden', 'correspondentie_waardenbereik', 'correspondentie_waarden', 'correspondentie_afgeleidebereik', 'correspondentie_afgeleide_waarde', 'calculus', 'grootheid_ongelijkheid', 'afgeleide_ongelijkheid'];
        const nieuweGroepen: BeschrijvingGroepData[] = [];
        const nieuweAlleRegels: BeschrijvingRegel[] = [];

        // Eerst bepalen of er in deze run echt nieuwe regels bijgekomen zijn (niet in this.alleRegels)
        let erZijnNieuweRegelsInDezeRun = false;
        if (!isEersteVulling) {
            for (const groep of groepsNamen) {
                const oudeGroepRegels = this.alleRegels.filter(b => b.groep === groep && b.status !== 'verwijderd' && !b.condtag);
                const nieuweGroepRegels = normaleSet.filter(n => n.groep === groep);

                const verwerkteOudIndexen = new Set<number>();
                for (const nieuw of nieuweGroepRegels) {
                    const oudIndex = oudeGroepRegels.findIndex((o, i) => this.vergelijkRegels(o.regel, nieuw.regel) && !verwerkteOudIndexen.has(i));
                    if (oudIndex !== -1) {
                        verwerkteOudIndexen.add(oudIndex);
                    } else {
                        erZijnNieuweRegelsInDezeRun = true;
                        break;
                    }
                }
                if (erZijnNieuweRegelsInDezeRun) break;
            }

            if (!erZijnNieuweRegelsInDezeRun) {
                // Check ook de conditie-sets
                for (const [condtag, data] of conditieSetsData.entries()) {
                    const checkSet = (regels: BeschrijvingRegel[], subGroep: 'cond' | 'cons') => {
                        for (const nieuw of regels) {
                            const oudRegel = this.alleRegels.find(o =>
                                o.condtag === condtag &&
                                o.condstate === subGroep &&
                                this.vergelijkRegels(o.regel, nieuw.regel) &&
                                o.status !== 'verwijderd'
                            );
                            if (!oudRegel) {
                                erZijnNieuweRegelsInDezeRun = true;
                                break;
                            }
                        }
                    };
                    checkSet(data.cond, 'cond');
                    if (erZijnNieuweRegelsInDezeRun) break;
                    checkSet(data.cons, 'cons');
                    if (erZijnNieuweRegelsInDezeRun) break;
                }
            }
        }

        // Als er echt nieuwe zinnen zijn in deze run, dan moeten de oude "nieuwe" zinnen niet meer vet zijn.
        if (erZijnNieuweRegelsInDezeRun) {
            this.vetgedrukteRegelIds.clear();
        }

        for (const groep of groepsNamen) {
            // Filter oude regels die bij deze groep horen, maar SLUIT regels met een condtag uit.
            // Die horen namelijk in de conditiesets thuis en mogen niet als 'verwijderd' in de normale groepen verschijnen.
            const oudeGroepRegels = this.alleRegels.filter(b => b.groep === groep && b.status !== 'verwijderd' && !b.condtag);
            const nieuweGroepRegels = normaleSet.filter(n => n.groep === groep);

            const groepResultaat: BeschrijvingRegel[] = [];
            const verwerkteOudIndexen = new Set<number>();

            // We volgen de volgorde van de nieuwe set om positienummers te bepalen.
            nieuweGroepRegels.forEach((nieuw, index) => {
                const oudIndex = oudeGroepRegels.findIndex((o, i) => this.vergelijkRegels(o.regel, nieuw.regel) && !verwerkteOudIndexen.has(i));
                const positieNummer = index + 1;

                if (oudIndex !== -1) {
                    const oud = oudeGroepRegels[oudIndex];
                    const bijgewerkteRegel: BeschrijvingRegel = {
                        ...nieuw,
                        id: oud.id,
                        status: 'bekend',
                        isVet: oud.id ? this.vetgedrukteRegelIds.has(oud.id) : false,
                        positieNummer
                    };
                    groepResultaat.push(bijgewerkteRegel);
                    nieuweAlleRegels.push(bijgewerkteRegel);
                    verwerkteOudIndexen.add(oudIndex);
                } else {
                    const id = `regel-${++TekstBeschrijvingComponent.idTeller}`;
                    const nieuweRegel: BeschrijvingRegel = {
                        ...nieuw,
                        id,
                        status: 'nieuw',
                        isVet: !isEersteVulling,
                        positieNummer
                    };
                    if (nieuweRegel.isVet) {
                        this.vetgedrukteRegelIds.add(id);
                    }
                    groepResultaat.push(nieuweRegel);
                    nieuweAlleRegels.push(nieuweRegel);
                }
            });

            // Voeg verwijderde regels toe met hun oorspronkelijke positienummer.
            oudeGroepRegels.forEach((oud, i) => {
                if (!verwerkteOudIndexen.has(i)) {
                    const verwijderdeRegel: BeschrijvingRegel = {...oud, status: 'verwijderd'};
                    groepResultaat.push(verwijderdeRegel);
                    nieuweAlleRegels.push(verwijderdeRegel);
                }
            });

            // Sorteer de resultaten binnen de groep:
            // 1. Op positieNummer (oplopend)
            // 2. Bij gelijk nummer: verwijderd < bekend < nieuw
            groepResultaat.sort((a, b) => {
                const posA = a.positieNummer ?? 0;
                const posB = b.positieNummer ?? 0;
                if (posA !== posB) {
                    return posA - posB;
                }
                const statusOrder = {'verwijderd': 0, 'bekend': 1, 'nieuw': 2};
                return statusOrder[a.status] - statusOrder[b.status];
            });

            if (groepResultaat.length > 0) {
                nieuweGroepen.push({
                    titel: groep,
                    regels: groepResultaat
                });
            }
        }

        // Verwerk conditiesets
        conditieSetsData.forEach((set, condtag) => {
            const verwerkSetRegels = (regels: BeschrijvingRegel[], subGroep: 'cond' | 'cons') => {
                const result: BeschrijvingRegel[] = [];
                // Net als bij de gewone groepen mag een oude regel maar één nieuwe regel zijn id
                // geven: twee gelijkluidende zinnen in dezelfde set (twee grootheden met dezelfde
                // naam) kregen anders allebei hetzelfde id, en dat zijn dubbele track-sleutels in de
                // @for (NG0955, bevinding golf a2 stap 2, test 3).
                const oudeSetRegels = this.alleRegels.filter(o => o.condtag === condtag && o.condstate === subGroep && o.status !== 'verwijderd');
                const verwerkteOudIndexen = new Set<number>();
                regels.forEach((nieuw, index) => {
                    const oudIndex = oudeSetRegels.findIndex((o, i) => this.vergelijkRegels(o.regel, nieuw.regel) && !verwerkteOudIndexen.has(i));
                    const oudRegel = oudIndex === -1 ? undefined : oudeSetRegels[oudIndex];
                    if (oudIndex !== -1) {
                        verwerkteOudIndexen.add(oudIndex);
                    }
                    const positieNummer = index + 1;

                    if (oudRegel) {
                        const bijgewerkteRegel: BeschrijvingRegel = {
                            ...nieuw,
                            id: oudRegel.id,
                            status: 'bekend',
                            isVet: oudRegel.id ? this.vetgedrukteRegelIds.has(oudRegel.id) : false,
                            positieNummer,
                            condtag,
                            condstate: subGroep
                        };
                        result.push(bijgewerkteRegel);
                        nieuweAlleRegels.push(bijgewerkteRegel);
                    } else {
                        const id = `regel-${++TekstBeschrijvingComponent.idTeller}`;
                        const nieuweRegel: BeschrijvingRegel = {
                            ...nieuw,
                            id,
                            status: 'nieuw',
                            isVet: !isEersteVulling,
                            positieNummer,
                            condtag,
                            condstate: subGroep
                        };
                        if (nieuweRegel.isVet) {
                            this.vetgedrukteRegelIds.add(id);
                        }
                        result.push(nieuweRegel);
                        nieuweAlleRegels.push(nieuweRegel);
                    }
                });

                // Verwijderde regels binnen conditieset
                this.alleRegels.forEach(oud => {
                    if (oud.status !== 'verwijderd' && oud.condtag === condtag && oud.condstate === subGroep) {
                        // Alleen als de conditieset zelf behouden blijft (dat is hier het geval, anders waren we niet in deze loop),
                        // voegen we de individuele regels als 'verwijderd' toe voor de animatie.
                        if (!result.some(r => r.id === oud.id)) {
                            const verwijderdeRegel: BeschrijvingRegel = {...oud, status: 'verwijderd'};
                            result.push(verwijderdeRegel);
                            nieuweAlleRegels.push(verwijderdeRegel);
                        }
                    }
                });

                result.sort((a, b) => {
                    const posA = a.positieNummer ?? 0;
                    const posB = b.positieNummer ?? 0;
                    if (posA !== posB) return posA - posB;
                    const statusOrder = {'verwijderd': 0, 'bekend': 1, 'nieuw': 2};
                    return statusOrder[a.status] - statusOrder[b.status];
                });

                return result;
            };

            nieuweGroepen.push({
                titel: condtag,
                condtag: condtag,
                condities: verwerkSetRegels(set.cond, 'cond'),
                consequenties: verwerkSetRegels(set.cons, 'cons')
            });
        });

        // 3. Verwijderde conditiesets die GEEN consequenties meer hebben (direct weg)
        // We moeten voorkomen dat regels van verdwenen conditiesets in de normale flow belanden.
        // Omdat we hierboven alleen door conditieSetsData loopen (die alleen sets met cons > 0 bevat),
        // worden regels van verwijderde sets niet aan nieuweAlleRegels toegevoegd. 
        // Ze worden dus impliciet direct verwijderd omdat ze niet meer in de arrays voorkomen.

        this.groepen = nieuweGroepen;
        this.alleRegels = nieuweAlleRegels;

        // Na de animatie de verwijderde items echt weghalen
        if (!isEersteVulling) {
            setTimeout(() => {
                this.alleRegels = this.alleRegels.filter(b => b.status !== 'verwijderd');
                this.groepen = this.groepen.map(g => {
                    const result: BeschrijvingGroepData = {...g};
                    if (result.regels) result.regels = result.regels.filter(r => r.status !== 'verwijderd');
                    if (result.condities) result.condities = result.condities.filter(r => r.status !== 'verwijderd');
                    if (result.consequenties) result.consequenties = result.consequenties.filter(r => r.status !== 'verwijderd');
                    return result;
                }).filter(g => (g.regels && g.regels.length > 0) || (g.consequenties && g.consequenties.length > 0));

                super.doeUpdate();
                this.scrollToLastNewOrChanged();
            }, 1500);
        }
    }

    /**
     * Vergelijkt twee regel-fragmenten arrays.
     * @private
     */
    private vergelijkRegels(a: BeschrijvingFragment[], b: BeschrijvingFragment[]): boolean {
        if (a.length !== b.length) return false;
        return a.every((frag, i) => frag.tekst === b[i].tekst && !!frag.isGrootheid === !!b[i].isGrootheid);
    }

    /**
     * Formatteert een lijst met namen tot een zin met comma's en "en".
     * @param namen De lijst met namen.
     * @returns De geformatteerde lijst.
     * @private
     */
    private formatteerLijst(namen: string[]): string {
        if (namen.length === 0) return '';
        if (namen.length === 1) return namen[0];
        const en = this.translate.instant('TEKSTBESCHRIJVING.EN');
        return `${namen.slice(0, -1).join(', ')}${en}${namen.at(-1)}`;
    }

    /**
     * Voegt meerdere sets fragmenten (zinnen of zinsdelen) samen tot één vloeiende zin.
     * De verbinding tussen de sets is een komma, behalve bij de laatste set, daar is het " en ".
     * @param sets De sets fragmenten die samengevoegd moeten worden.
     * @param glueNaEerste De verbinding die gebruikt moet worden na de eerste set (standaard een komma). Als dit leeg is, wordt er geen glue toegevoegd.
     * @returns De gecombineerde fragmenten.
     * @private
     */
    private voegFragmentenSamen(sets: BeschrijvingFragment[][], glueNaEerste: string = ', '): BeschrijvingFragment[] {
        if (sets.length === 0) return [];
        if (sets.length === 1) return sets[0];

        const result: BeschrijvingFragment[] = [];
        const en = this.translate.instant('TEKSTBESCHRIJVING.EN');

        sets.forEach((set, index) => {
            if (index > 0) {
                const isLaatste = index === sets.length - 1;
                // De regel: de glue is een komma, behalve bij de laatste set, daar is het " en ".
                let glue = ', ';
                if (isLaatste) {
                    glue = ` ${en} `;
                }

                // Als glueNaEerste specifiek is overschreven (ook naar een lege string),
                // gebruiken we die alleen na de eerste set (index === 1).
                if (index === 1 && glueNaEerste !== ', ') {
                    glue = glueNaEerste;
                }

                if (glue) {
                    result.push({tekst: glue});
                }
            }

            // Voeg de fragmenten van de huidige set toe
            set.forEach((f, fIndex) => {
                let tekst = f.tekst;
                // Als dit niet de eerste set is, en het is het eerste fragment van deze set,
                // zorgen we dat het met een kleine letter begint als het tekst is.
                // Uitzondering: als de glue expliciet een spatie is (geen interpunctie), gaan we er van uit dat de zin gewoon doorloopt.
                if (index > 0 && fIndex === 0 && tekst.length > 0 && glueNaEerste !== ' ') {
                    const eersteKarakter = tekst.charAt(0);
                    // We maken alleen het eerste karakter lowercase als de rest van het woord niet al uit hoofdletters bestaat (afkorting).
                    if (tekst.length === 1 || (tekst.charAt(1).toLowerCase() === tekst.charAt(1))) {
                        tekst = eersteKarakter.toLowerCase() + tekst.slice(1);
                    }
                }
                result.push({...f, tekst});
            });
        });

        return result;
    }

    /**
     * Formatteert een tekst tot een correcte zin (hoofdletter en punt).
     * @param fragmenten De fragmenten van de zin of platte tekst.
     * @returns De geformatteerde zin-fragmenten.
     * @private
     */
    private zin(fragmenten: BeschrijvingFragment[] | string): BeschrijvingFragment[] {
        if (typeof fragmenten === 'string') {
            fragmenten = [{tekst: fragmenten}];
        }
        if (!fragmenten || fragmenten.length === 0) return [];

        const result: BeschrijvingFragment[] = fragmenten.map(f => ({...f}));
        const eersteTekst = result[0].tekst;
        if (eersteTekst.length > 0) {
            result[0].tekst = eersteTekst.charAt(0).toUpperCase() + eersteTekst.slice(1);
        }

        const laatsteFragment = result[result.length - 1];
        if (laatsteFragment.tekst.length > 0 && !laatsteFragment.tekst.endsWith('.')) {
            laatsteFragment.tekst += '.';
        }

        return result;
    }


    /**
     * Beschrijft entiteiten, agents en aannames die geen configuratie én geen grootheid hebben.
     * Deze verschijnen als losse namen bovenaan de beschrijving.
     */
    private beschrijfLosseEntiteiten(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const entiteiten = this.dyna!.typeNodes('entity', condtag, condstate);
        const agents = this.dyna!.typeNodes('agent', condtag, condstate);
        const assumpties = this.dyna!.typeNodes('assumption', condtag, condstate);
        const alleEntiteitNodes = this.sorteerOpLocatie([...entiteiten.toArray(), ...agents.toArray(), ...assumpties.toArray()]);

        // Verzamel IDs van entiteiten die in een configuratie-relatie voorkomen (als bron of doel).
        const entiteitenInConfiguratie = new Set<string>();
        this.dyna!.typeNodes('configuration').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            entiteitenInConfiguratie.add(node.data('from'));
            entiteitenInConfiguratie.add(node.data('to'));
        });

        // Verzamel IDs van entiteiten die minstens één grootheid hebben.
        const entiteitenMetGrootheid = new Set<string>();
        this.dyna!.typeNodes('quantity').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            const parent = this.dyna!.getParent(node);
            if (parent) entiteitenMetGrootheid.add(parent.id());
        });

        const regels: BeschrijvingRegel[] = [];
        for (const node of alleEntiteitNodes) {
            const nodeId = node.id();
            if (entiteitenInConfiguratie.has(nodeId)) continue;
            if (entiteitenMetGrootheid.has(nodeId)) continue;

            regels.push({
                regel: this.zin([{tekst: this.dyna!.nodeName(node)}]),
                status: 'nieuw',
                groep: 'entiteiten',
                nodes: [nodeId],
                sorteerNode: node
            });
        }

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Helper bij geneerBeschrijving: zoek alle nodes met data.type "configuration"
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfConfiguraties(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        // We verzamelen eerst alle entiteit-achtige nodes (entiteiten, agents, assumpties) die matchen met de condtag.
        const entiteiten = this.dyna!.typeNodes('entity', condtag, condstate);
        const agents = this.dyna!.typeNodes('agent', condtag, condstate);
        const assumpties = this.dyna!.typeNodes('assumption', condtag, condstate);
        const alleEntiteitNodes = this.sorteerOpLocatie([...entiteiten.toArray(), ...agents.toArray(), ...assumpties.toArray()]);

        // Verzamelen alle configuratie-relaties die matchen met de condtag.
        const alleConfigRelaties: Array<{ from: string, to: string, naam: string, id: string, node: any }> = [];
        this.dyna!.typeNodes('configuration').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            alleConfigRelaties.push({
                from: node.data('from'),
                to: node.data('to'),
                naam: this.dyna!.nodeName(node),
                id: node.id(),
                node: node
            });
        });

        const regels: BeschrijvingRegel[] = [];
        const verwerkteConfigRelatieIds = new Set<string>();
        const verwerkteEntiteitIds = new Set<string>();

        // We lopen nu door de gesorteerde entiteit-nodes.
        for (const node of alleEntiteitNodes) {
            const nodeId = node.id();
            if (verwerkteEntiteitIds.has(nodeId)) continue;

            // Zoek alle configuratie-relaties waar deze node een bron (from) is.
            // (Als een node alleen een doel (to) is, dan wordt deze later beschreven of door een andere bron meegenomen).
            const relatiesVanafHier = alleConfigRelaties.filter(rel => rel.from === nodeId && !verwerkteConfigRelatieIds.has(rel.id));

            if (relatiesVanafHier.length > 0) {
                // We hebben relaties vanaf deze node. We passen hier de M:N groepering toe, 
                // maar beperkt tot de huidige bron(nen) om de sortering van de bronnen te respecteren.

                // 1. Zoek naar andere bronnen die EXACT dezelfde relaties (actie + doel) hebben als deze node.
                // Dit zorgt ervoor dat we "A en B doen X met C" krijgen op de plek waar A (de eerste van A en B) staat.
                const actieDoelSleutel = (nodeId: string) => {
                    return alleConfigRelaties
                        .filter(rel => rel.from === nodeId && !verwerkteConfigRelatieIds.has(rel.id))
                        .map(rel => `${rel.naam}|${rel.to}`)
                        .sort()
                        .join('||');
                };

                const huidigeSleutel = actieDoelSleutel(nodeId);
                const gedeeldeBronnen = alleEntiteitNodes.filter(n =>
                    !verwerkteEntiteitIds.has(n.id()) && actieDoelSleutel(n.id()) === huidigeSleutel
                );

                // Verzamel alle relatie-IDs die we nu gaan beschrijven.
                const teVerwerkenRelaties = alleConfigRelaties.filter(rel =>
                    gedeeldeBronnen.some(b => b.id() === rel.from) && !verwerkteConfigRelatieIds.has(rel.id)
                );

                // Omdat we weten dat alle 'gedeeldeBronnen' dezelfde actieDoel combinaties hebben,
                // kunnen we ze samenvoegen.

                // Groepeer op actieset en doelen.
                const actieSetPerDoelSet: Record<string, { acties: string[], tos: string[], ids: string[] }> = {};
                for (const rel of teVerwerkenRelaties) {
                    // We weten dat de relatie vanaf een van de gedeeldeBronnen komt.
                    const doelId = rel.to;
                    const actie = rel.naam;

                    // We groeperen eerst op doel om te zien welke acties daarheen gaan.
                    if (!actieSetPerDoelSet[doelId]) {
                        actieSetPerDoelSet[doelId] = {acties: [], tos: [doelId], ids: []};
                    }
                    if (!actieSetPerDoelSet[doelId].acties.includes(actie)) {
                        actieSetPerDoelSet[doelId].acties.push(actie);
                    }
                    actieSetPerDoelSet[doelId].ids.push(rel.id);
                }

                // Nu groeperen we doelen die dezelfde actieset hebben van deze bronnen.
                const finaleGroepen: Array<{ froms: string[], tos: string[], acties: string[], ids: string[] }> = [];
                const verwerkteDoelenInSessie = new Set<string>();

                for (const doelId in actieSetPerDoelSet) {
                    if (verwerkteDoelenInSessie.has(doelId)) continue;
                    const huidigeActies = actieSetPerDoelSet[doelId].acties.sort().join('|');

                    const gelijkeDoelen = Object.keys(actieSetPerDoelSet).filter(dId =>
                        actieSetPerDoelSet[dId].acties.sort().join('|') === huidigeActies
                    );

                    finaleGroepen.push({
                        froms: gedeeldeBronnen.map(b => b.id()),
                        tos: gelijkeDoelen,
                        acties: actieSetPerDoelSet[doelId].acties,
                        ids: gelijkeDoelen.flatMap(dId => actieSetPerDoelSet[dId].ids)
                    });

                    gelijkeDoelen.forEach(dId => verwerkteDoelenInSessie.add(dId));
                }

                // Maak regels voor deze groepen.
                for (const groep of finaleGroepen) {
                    const gesorteerdeFroms = this.sorteerOpLocatie(groep.froms.map(id => this.dyna!.getNode(id)));
                    const gesorteerdeTos = this.sorteerOpLocatie(groep.tos.map(id => this.dyna!.getNode(id)));

                    const fromNamen = gesorteerdeFroms.map(n => this.dyna!.nodeName(n));
                    const actieNamen = groep.acties.sort();
                    const toNamen = gesorteerdeTos.map(n => this.dyna!.nodeName(n));

                    regels.push({
                        regel: this.zin(`${this.formatteerLijst(fromNamen)} ${this.formatteerLijst(actieNamen)} ${this.formatteerLijst(toNamen)}`),
                        status: 'nieuw',
                        groep: 'configuraties',
                        nodes: [...groep.froms, ...groep.tos, ...groep.ids],
                        sorteerNode: gesorteerdeFroms[0]
                    });

                    groep.ids.forEach(id => verwerkteConfigRelatieIds.add(id));
                    groep.tos.forEach(id => verwerkteEntiteitIds.add(id));
                }

                gedeeldeBronnen.forEach(b => verwerkteEntiteitIds.add(b.id()));
            } else {
                // De node heeft geen uitgaande configuratie-relaties (meer).
                // Controleer of de node als doel in een configuratie zit die we nog niet verwerkt hebben.
                // Als dat zo is, wachten we tot de bron van die relatie aan de beurt is.
                const isDoelInToekomstigeRelatie = alleConfigRelaties.some(rel => rel.to === nodeId && !verwerkteConfigRelatieIds.has(rel.id));

                if (!isDoelInToekomstigeRelatie) {
                    // De node is geen bron en geen (toekomstig) doel van een configuratie.
                    // We markeren de node als verwerkt; losse entiteiten worden beschreven in beschrijfLosseEntiteiten.
                    verwerkteEntiteitIds.add(nodeId);
                }
                // Als het wel een doel is in een toekomstige relatie, doen we nu niets. 
                // De node wordt beschreven wanneer de bron aan de beurt is.
            }
        }

        // Als er onverhoopt nog relaties over zijn (bijv. circulaire configuraties zonder duidelijke bron),
        // beschrijven we die hier alsnog (vangnet).
        const resterendeRelaties = alleConfigRelaties.filter(rel => !verwerkteConfigRelatieIds.has(rel.id));
        if (resterendeRelaties.length > 0) {
            // Pas de oude M:N groepering toe op wat overblijft
            const vanNaarActies: Record<string, { from: string, to: string, acties: string[], ids: string[] }> = {};
            for (const rel of resterendeRelaties) {
                const sleutel = `${rel.from}->${rel.to}`;
                if (!vanNaarActies[sleutel]) vanNaarActies[sleutel] = {from: rel.from, to: rel.to, acties: [], ids: []};
                if (!vanNaarActies[sleutel].acties.includes(rel.naam)) vanNaarActies[sleutel].acties.push(rel.naam);
                vanNaarActies[sleutel].ids.push(rel.id);
            }
            const perActieSet: Record<string, Array<{ from: string, to: string, ids: string[] }>> = {};
            for (const sleutel in vanNaarActies) {
                const item = vanNaarActies[sleutel];
                const actieSleutel = item.acties.sort().join('|');
                if (!perActieSet[actieSleutel]) perActieSet[actieSleutel] = [];
                perActieSet[actieSleutel].push({from: item.from, to: item.to, ids: item.ids});
            }
            for (const actieSleutel in perActieSet) {
                const relatiesInSet = perActieSet[actieSleutel];
                const actieNamen = actieSleutel.split('|');
                const tosPerFrom: Record<string, { tos: string[], ids: string[] }> = {};
                for (const rel of relatiesInSet) {
                    if (!tosPerFrom[rel.from]) tosPerFrom[rel.from] = {tos: [], ids: []};
                    tosPerFrom[rel.from].tos.push(rel.to);
                    tosPerFrom[rel.from].ids.push(...rel.ids);
                }
                const groepen: Record<string, { froms: string[], tos: string[], ids: string[] }> = {};
                for (const from in tosPerFrom) {
                    const toSleutel = tosPerFrom[from].tos.sort().join('|');
                    if (!groepen[toSleutel]) groepen[toSleutel] = {froms: [], tos: tosPerFrom[from].tos, ids: []};
                    groepen[toSleutel].froms.push(from);
                    groepen[toSleutel].ids.push(...tosPerFrom[from].ids);
                }
                for (const toSleutel in groepen) {
                    const groep = groepen[toSleutel];
                    const gesorteerdeFroms = this.sorteerOpLocatie(groep.froms.map(id => this.dyna!.getNode(id)));
                    const gesorteerdeTos = this.sorteerOpLocatie(groep.tos.map(id => this.dyna!.getNode(id)));
                    regels.push({
                        regel: this.zin(`${this.formatteerLijst(gesorteerdeFroms.map(n => this.dyna!.nodeName(n)))} ${this.formatteerLijst(actieNamen)} ${this.formatteerLijst(gesorteerdeTos.map(n => this.dyna!.nodeName(n)))}`),
                        status: 'nieuw',
                        groep: 'configuraties',
                        nodes: [...groep.froms, ...groep.tos, ...groep.ids],
                        sorteerNode: gesorteerdeFroms[0]
                    });
                }
            }
        }

        // Sorteer de finale lijst met regels nogmaals op basis van de schermlocatie van hun sorteerNode.
        // Dit garandeert dat zelfs als de loop-volgorde door groeperingen licht verschoof, de regels onderling goed staan.
        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Zoekt alle grootheden op en beschrijft ze.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfGrootheden(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        // Verzamelen welke grootheden bij welke entiteit horen door te groeperen op de parent-node.
        const groothedenPerEntiteit: Record<string, { nodes: any[] }> = {};

        // We filteren quantity-nodes zelf als zij (de quantity zelf) in de conditieset zitten.
        this.sorteerOpLocatie(this.dyna!.typeNodes('quantity')).forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;

            const parent = this.dyna!.getParent(node);
            if (!parent) return;

            const parentId = parent.id();
            if (!groothedenPerEntiteit[parentId]) {
                groothedenPerEntiteit[parentId] = {nodes: []};
            }

            groothedenPerEntiteit[parentId].nodes.push(node);
        });

        // Voer een M:N groepering uit om te bepalen welke entiteiten exact dezelfde set grootheden bezitten.
        // We gebruiken de namen van de grootheden als sleutel voor de set.
        const entiteitenPerGrootheidSet: Record<string, { entiteiten: any[], nodesPerEntiteit: any[][] }> = {};

        for (const entiteitId in groothedenPerEntiteit) {
            const set = groothedenPerEntiteit[entiteitId];
            // Sorteer namen alfabetisch voor de sleutel, zodat sets met dezelfde namen bij elkaar komen.
            const namenSleutel = set.nodes.map(n => this.dyna!.nodeName(n)).sort().join('|');

            if (!entiteitenPerGrootheidSet[namenSleutel]) {
                entiteitenPerGrootheidSet[namenSleutel] = {entiteiten: [], nodesPerEntiteit: []};
            }

            entiteitenPerGrootheidSet[namenSleutel].entiteiten.push(this.dyna!.getNode(entiteitId));
            // We bewaren de nodes van deze specifieke entiteit om ze later per groep te kunnen sorteren op x-positie.
            entiteitenPerGrootheidSet[namenSleutel].nodesPerEntiteit.push(set.nodes);
        }

        // Maak de regels
        const gesorteerdeGrootheidSetSleutels = Object.keys(entiteitenPerGrootheidSet).sort((aSleutel, bSleutel) => {
            const nodeA = this.sorteerOpLocatie(entiteitenPerGrootheidSet[aSleutel].entiteiten)[0];
            const nodeB = this.sorteerOpLocatie(entiteitenPerGrootheidSet[bSleutel].entiteiten)[0];
            return this.sorteerOpLocatie([nodeA, nodeB])[0] === nodeA ? -1 : 1;
        });

        for (const sleutel of gesorteerdeGrootheidSetSleutels) {
            const groep = entiteitenPerGrootheidSet[sleutel];
            const gesorteerdeEntiteiten = this.sorteerOpLocatie(groep.entiteiten);
            const entiteitNamen = gesorteerdeEntiteiten.map(node => this.dyna!.nodeName(node));
            const entiteitTekst = this.formatteerLijst(entiteitNamen);

            // Sorteer de grootheden van de eerste entiteit in de groep op x-positie (conform richtlijn).
            // Omdat alle entiteiten in deze groep dezelfde set grootheden hebben (op basis van naam),
            // is de relatieve x-volgorde van de grootheden van de eerste entiteit representatief.
            const voorbeeldNodes = groep.nodesPerEntiteit[0];
            const gesorteerdeGrootheidNodes = voorbeeldNodes.sort((a, b) => a.position().x - b.position().x);
            const grootheidNamen = gesorteerdeGrootheidNodes.map(n => this.dyna!.nodeName(n));

            const heeftHebben = groep.entiteiten.length > 1 ? this.translate.instant('TEKSTBESCHRIJVING.HEBBEN') : this.translate.instant('TEKSTBESCHRIJVING.HEEFT');
            const term = grootheidNamen.length > 1 ? this.translate.instant('TEKSTBESCHRIJVING.GROOTHEDEN') : this.translate.instant('TEKSTBESCHRIJVING.GROOTHEID');

            const entiteitFragmenten: BeschrijvingFragment[] = [{tekst: `${entiteitTekst} ${heeftHebben}${term}`}];
            const grootheidSets: BeschrijvingFragment[][] = grootheidNamen.map(naam => [{
                tekst: naam,
                isGrootheid: true
            }]);

            regels.push({
                regel: this.zin(this.voegFragmentenSamen([entiteitFragmenten, ...grootheidSets], ' ')),
                status: 'nieuw',
                groep: 'grootheden',
                nodes: [...groep.entiteiten.map(n => n.id()), ...groep.nodesPerEntiteit.flat().map(n => n.id())],
                sorteerNode: gesorteerdeEntiteiten[0]
            });
        }

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de correspondentie tussen waardebereiken.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfCorrespondentieWaardenbereik(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        this.dyna!.typeNodes('correspondence_qs').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            const type = node.data('type');
            const isReverse = type.includes('reverse');

            // De correspondence_qs node zit tussen twee quantity_space nodes.
            // We zoeken de quantity nodes die verbonden zijn met deze node via Dynascape.
            const result = this.dyna!.getQuantitiesForCorrespondenceQS(node as any);

            if (result) {
                const {q1, q2} = result;

                // Sorteer q1 en q2 op locatie om te bepalen wie er eerst in de zin komt
                const gesorteerd = this.sorteerOpLocatie([q1, q2]);
                const first = gesorteerd[0];
                const second = gesorteerd[1];

                const n1 = this.dyna!.nodeName(first);
                const n2 = this.dyna!.nodeName(second);

                const p1 = this.dyna!.getParent(first);
                const p2 = this.dyna!.getParent(second);

                const en1 = p1 ? this.dyna!.nodeName(p1) : '';
                const en2 = p2 ? this.dyna!.nodeName(p2) : '';

                const termVertaald = isReverse ? this.translate.instant('TEKSTBESCHRIJVING.GESPIEGELDE') : this.translate.instant('TEKSTBESCHRIJVING.CORRESPONDERENDE');
                const regel: BeschrijvingFragment[] = [];

                if (n1 === n2 && en1 && en2) {
                    // Als de grootheden exact dezelfde naam hebben: <grootheid> van <entiteit1> en <entiteit2>
                    // const van = this.translate.instant('TEKSTBESCHRIJVING.VAN');
                    const en = this.translate.instant('TEKSTBESCHRIJVING.EN');
                    const waarden = this.translate.instant('TEKSTBESCHRIJVING.WAARDEN_MEERVOUD');
                    const hebben = this.translate.instant('TEKSTBESCHRIJVING.HEBBEN');
                    regel.push({tekst: n1, isGrootheid: true});
                    // regel.push({tekst: `${van}${en1}${en}${en2}${hebben}${termVertaald} ${waarden}`});
                    regel.push({tekst: ` (${en1}${en}${en2})${hebben}${termVertaald} ${waarden}`});
                } else {
                    const waarden = this.translate.instant('TEKSTBESCHRIJVING.WAARDEN_MEERVOUD');
                    const hebben = this.translate.instant('TEKSTBESCHRIJVING.HEBBEN');
                    regel.push(...this.tweeGroothedenMetEntiteit(first, second));
                    regel.push({tekst: `${hebben}${termVertaald} ${waarden}`});
                }

                regels.push({
                    regel: this.zin(regel),
                    status: 'nieuw',
                    groep: 'correspondentie_waardenbereik',
                    nodes: [node.id(), q1.id(), q2.id()],
                    sorteerNode: first
                });
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de correspondentie tussen specifieke waarden in de quantity spaces.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfCorrespondentieWaarden(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        this.dyna!.typeNodes('correspondence_qv').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            const type = node.data('type');
            const isDirected = type === 'correspondence_qv_directed';

            const result = this.dyna!.getRelationArguments(node as any);

            if (result) {
                const {from: fromNode, to: toNode} = result;

                const q1 = this.dyna!.getQuantityForQSValue(fromNode);
                const q2 = this.dyna!.getQuantityForQSValue(toNode);

                if (q1 && q2) {
                    // Sorteer op locatie als het ongericht is
                    let firstQ = q1;
                    let secondQ = q2;
                    let firstV = fromNode;
                    let secondV = toNode;

                    if (!isDirected) {
                        const gesorteerd = this.sorteerOpLocatie([q1, q2]);
                        if (gesorteerd[0] !== q1) {
                            firstQ = q2;
                            secondQ = q1;
                            firstV = toNode;
                            secondV = fromNode;
                        }
                    }

                    const w1 = this.dyna!.nodeName(firstV, true);
                    const w2 = this.dyna!.nodeName(secondV, true);

                    const als = this.translate.instant('TEKSTBESCHRIJVING.ALS');
                    const danHeeft = this.translate.instant('TEKSTBESCHRIJVING.DAN_HEEFT', {waarde: w1});

                    const regelHandmatig: BeschrijvingFragment[] = [
                        {tekst: als}
                    ];

                    //geen samenvoeging binnen entiteit, dat is niet leesbaar
                    regelHandmatig.push(...this.grootheidMetEntiteit(firstQ));
                    regelHandmatig.push({tekst: danHeeft});
                    regelHandmatig.push(...this.grootheidMetEntiteit(secondQ));
                    regelHandmatig.push({tekst: ` ${this.translate.instant('TEKSTBESCHRIJVING.WAARDE')} ${w2}`});
                    if (!isDirected) {
                        regelHandmatig.push({tekst: this.translate.instant('TEKSTBESCHRIJVING.EN_OMGEKEERD')});
                    }


                    regels.push({
                        regel: this.zin(regelHandmatig),
                        status: 'nieuw',
                        groep: 'correspondentie_waarden',
                        nodes: [node.id(), fromNode.id(), toNode.id(), q1.id(), q2.id()],
                        sorteerNode: firstQ
                    });
                }
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de correspondentie tussen waardebereiken van afgeleiden.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfCorrespondentieAfgeleidebereik(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        this.dyna!.typeNodes('correspondence_dqs').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            const type = node.data('type');
            const isReverse = type.includes('reverse');

            // De correspondence_dqs node zit tussen twee derivative nodes.
            const result = this.dyna!.getQuantitiesForCorrespondenceDQS(node as any);

            if (result) {
                const {q1, q2} = result;

                // Sorteer op locatie
                const gesorteerd = this.sorteerOpLocatie([q1, q2]);
                const first = gesorteerd[0];
                const second = gesorteerd[1];

                const n1 = this.dyna!.nodeName(first);
                const n2 = this.dyna!.nodeName(second);

                const p1 = this.dyna!.getParent(first);
                const p2 = this.dyna!.getParent(second);

                const en1 = p1 ? this.dyna!.nodeName(p1) : '';
                const en2 = p2 ? this.dyna!.nodeName(p2) : '';

                const termVertaald = isReverse ? this.translate.instant('TEKSTBESCHRIJVING.TEGENGESTELD') : this.translate.instant('TEKSTBESCHRIJVING.OVEREEKOMSTIG');
                const regel: BeschrijvingFragment[] = [];

                if (n1 === n2 && en1 && en2) {
                    // Als de grootheden exact dezelfde naam hebben: <grootheid> van <entiteit1> en <entiteit2>
                    // const van = this.translate.instant('TEKSTBESCHRIJVING.VAN');
                    const en = this.translate.instant('TEKSTBESCHRIJVING.EN');
                    const veranderen = this.translate.instant('TEKSTBESCHRIJVING.VERANDEREN');
                    regel.push({tekst: n1, isGrootheid: true});
                    // regel.push({tekst: `${van}${en1}${en}${en2}${veranderen} ${termVertaald}`});
                    regel.push({tekst: ` (${en1}${en}${en2})${veranderen} ${termVertaald}`});
                } else {
                    const veranderen = this.translate.instant('TEKSTBESCHRIJVING.VERANDEREN');
                    regel.push(...this.tweeGroothedenMetEntiteit(first, second));
                    regel.push({tekst: `${veranderen} ${termVertaald}`});
                }

                regels.push({
                    regel: this.zin(regel),
                    status: 'nieuw',
                    groep: 'correspondentie_afgeleidebereik',
                    nodes: [node.id(), q1.id(), q2.id()],
                    sorteerNode: first
                });
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de correspondentie tussen specifieke afgeleide waarden.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfCorrespondentieAfgeleideWaarde(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        this.dyna!.typeNodes('correspondence_dv').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            const type = node.data('type');
            const isDirected = type === 'correspondence_dv_directed';

            const result = this.dyna!.getRelationArguments(node as any);

            if (result) {
                const {from: fromNode, to: toNode} = result;

                const q1 = this.dyna!.getQuantityForDValue(fromNode);
                const q2 = this.dyna!.getQuantityForDValue(toNode);

                if (q1 && q2) {
                    // Sorteer op locatie als het ongericht is
                    let firstQ = q1;
                    let secondQ = q2;
                    let firstV = fromNode;
                    let secondV = toNode;

                    if (!isDirected) {
                        const gesorteerd = this.sorteerOpLocatie([q1, q2]);
                        if (gesorteerd[0] !== q1) {
                            firstQ = q2;
                            secondQ = q1;
                            firstV = toNode;
                            secondV = fromNode;
                        }
                    }

                    const vertaalWaarde = (vNode: any) => {
                        const vType = vNode.data('type');
                        switch (vType) {
                            case 'derivative_min':
                                return this.translate.instant('TEKSTBESCHRIJVING.AFNEMEN');
                            case 'derivative_zero':
                                return this.translate.instant('TEKSTBESCHRIJVING.GELIJK_BLIJVEN');
                            case 'derivative_plus':
                                return this.translate.instant('TEKSTBESCHRIJVING.TOENEMEN');
                            default:
                                return this.dyna!.nodeName(vNode, true);
                        }
                    };

                    const w1 = vertaalWaarde(firstV);
                    const w2 = vertaalWaarde(secondV);

                    const regel: BeschrijvingFragment[] = [];
                    const van = this.translate.instant('TEKSTBESCHRIJVING.VAN');
                    const en = this.translate.instant('TEKSTBESCHRIJVING.EN');

                    const p1 = this.dyna!.getParent(firstQ);
                    const p2 = this.dyna!.getParent(secondQ);

                    if (isDirected) {
                        // ${w1} van ${g1} en ${w2} van ${g2} komen tegelijk voor
                        // OF: ${w1} en ${w2} van ${e} komen tegelijk voor
                        if (p1 && p2 && p1.id() === p2.id()) {
                            regel.push({tekst: w1});
                            regel.push({tekst: en});
                            regel.push({tekst: w2});
                            regel.push({tekst: van});
                            regel.push(...this.grootheidMetEntiteit(firstQ)); // "grootheid1 van entiteit"
                            // Bovenstaande geeft "G1 van E". We willen eigenlijk "G1 en G2 van E".
                            // Laten we het hier ook handmatig doen voor precisie.
                            regel.length = 0;
                            regel.push({tekst: w1});
                            regel.push({tekst: en});
                            regel.push({tekst: w2});
                            regel.push({tekst: van});
                            regel.push({tekst: this.dyna!.nodeName(firstQ), isGrootheid: true});
                            regel.push({tekst: en});
                            regel.push({tekst: this.dyna!.nodeName(secondQ), isGrootheid: true});
                            regel.push({tekst: `${van}${this.dyna!.nodeName(p1)}`});
                        } else {
                            regel.push({tekst: w1});
                            regel.push({tekst: van});
                            regel.push(...this.grootheidMetEntiteit(firstQ));
                            regel.push({tekst: en});
                            regel.push({tekst: w2});
                            regel.push({tekst: van});
                            regel.push(...this.grootheidMetEntiteit(secondQ));
                        }
                        regel.push({tekst: this.translate.instant('TEKSTBESCHRIJVING.KOMEN_TEGELIJK_VOOR')});
                    } else {
                        // bij ${w1} van ${g1} geldt ${w2} van ${g2} en omgekeerd
                        const bij = this.translate.instant('TEKSTBESCHRIJVING.BIJ');
                        const geldt = this.translate.instant('TEKSTBESCHRIJVING.GELDT');
                        regel.push({tekst: bij});
                        if (p1 && p2 && p1.id() === p2.id()) {
                            regel.push({tekst: w1});
                            regel.push({tekst: en});
                            regel.push({tekst: w2});
                            regel.push({tekst: van});
                            regel.push({tekst: this.dyna!.nodeName(firstQ), isGrootheid: true});
                            regel.push({tekst: en});
                            regel.push({tekst: this.dyna!.nodeName(secondQ), isGrootheid: true});
                            regel.push({tekst: `${van}${this.dyna!.nodeName(p1)}`});
                            regel.push({tekst: this.translate.instant('TEKSTBESCHRIJVING.GELDT_DIT_TEGELIJK')});
                        } else {
                            regel.push({tekst: w1});
                            regel.push({tekst: van});
                            regel.push(...this.grootheidMetEntiteit(firstQ));
                            regel.push({tekst: geldt});
                            regel.push({tekst: w2});
                            regel.push({tekst: van});
                            regel.push(...this.grootheidMetEntiteit(secondQ));
                        }
                        regel.push({tekst: this.translate.instant('TEKSTBESCHRIJVING.EN_OMGEKEERD')});
                    }

                    regels.push({
                        regel: this.zin(regel),
                        status: 'nieuw',
                        groep: 'correspondentie_afgeleide_waarde',
                        nodes: [node.id(), fromNode.id(), toNode.id(), q1.id(), q2.id()],
                        sorteerNode: firstQ
                    });
                }
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft calc-elementen (q_calc en d_calc) die niet als argument in een ongelijkheid voorkomen.
     * De beschrijving volgt hetzelfde patroon als bij ongelijkheden: operand1 operator operand2.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfCalculus(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        // Verzamel alle node-ids die als argument (from/to) in een ongelijkheid of andere calculus voorkomen
        const inOngelijkheidIds = new Set<string>();
        for (const ineqType of ['q_ineq', 'd_ineq', 'q_calc', 'd_calc']) {
            this.dyna!.typeNodes(ineqType).forEach((ineqNode) => {
                const res = this.dyna!.getRelationArguments(ineqNode as any);
                if (res) {
                    inOngelijkheidIds.add(res.from.id());
                    inOngelijkheidIds.add(res.to.id());
                }
            });
        }

        // Vertaalt een q_calc-argument recursief naar fragmenten
        const vertaalQArgument = (argNode: any): BeschrijvingFragment[] => {
            const argType = argNode.data('type');
            if (this.dyna!.isSubtypeOf(argType, 'q_calc')) {
                const res = this.dyna!.getRelationArguments(argNode);
                if (res) {
                    let operator;
                    switch (argType) {
                        case 'q_calc_plus':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.PLUS');
                            break;
                        case 'q_calc_min':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.MIN');
                            break;
                        case 'q_calc_mult':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.KEER');
                            break;
                        case 'q_calc_div':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.GEDEELD_DOOR');
                            break;
                        default:
                            operator = '?';
                    }
                    return [
                        ...vertaalQArgument(res.from),
                        {tekst: ` ${operator} `},
                        ...vertaalQArgument(res.to)
                    ];
                }
            }
            if (argType === 'quantity') {
                return this.grootheidMetEntiteit(argNode);
            } else if (argType === 'quantity_space_point') {
                const q = this.dyna!.getQuantityForQSValue(argNode);
                const w = this.dyna!.nodeName(argNode, true);
                if (q) {
                    return [
                        {tekst: w},
                        {tekst: this.translate.instant('TEKSTBESCHRIJVING.VAN')},
                        ...this.grootheidMetEntiteit(q)
                    ];
                }
                return [{tekst: w}];
            }
            return [{tekst: this.dyna!.nodeName(argNode, true)}];
        };

        this.dyna!.typeNodes('q_calc').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            if (inOngelijkheidIds.has(node.id())) return;

            const result = this.dyna!.getRelationArguments(node as any);
            if (result) {
                const {from, to} = result;
                const type = node.data('type');
                let operator;
                switch (type) {
                    case 'q_calc_plus':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.PLUS');
                        break;
                    case 'q_calc_min':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.MIN');
                        break;
                    case 'q_calc_mult':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.KEER');
                        break;
                    case 'q_calc_div':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.GEDEELD_DOOR');
                        break;
                    default:
                        operator = '?';
                }
                const regel: BeschrijvingFragment[] = [
                    ...vertaalQArgument(from),
                    {tekst: ` ${operator} `},
                    ...vertaalQArgument(to)
                ];
                const sorteerNode = from.data('type') === 'quantity' ? from : (to.data('type') === 'quantity' ? to : null);
                regels.push({
                    regel: this.zin(regel),
                    status: 'nieuw',
                    groep: 'calculus',
                    nodes: [node.id(), from.id(), to.id()],
                    sorteerNode
                });
            }
        });

        // Vertaalt een d_calc-argument recursief naar fragmenten
        const vertaalDArgument = (argNode: any): BeschrijvingFragment[] => {
            const argType = argNode.data('type');
            if (this.dyna!.isSubtypeOf(argType, 'd_calc')) {
                const res = this.dyna!.getRelationArguments(argNode);
                if (res) {
                    let operator;
                    switch (argType) {
                        case 'd_calc_plus':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.PLUS');
                            break;
                        case 'd_calc_min':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.MIN');
                            break;
                        case 'd_calc_mult':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.KEER');
                            break;
                        case 'd_calc_div':
                            operator = this.translate.instant('TEKSTBESCHRIJVING.GEDEELD_DOOR');
                            break;
                        default:
                            operator = '?';
                    }
                    return [
                        ...vertaalDArgument(res.from),
                        {tekst: ` ${operator} `},
                        ...vertaalDArgument(res.to)
                    ];
                }
            }
            if (argType === 'derivative') {
                const q = this.dyna!.inElements(argNode, '[type="quantity"]').first();
                if (!q.empty()) {
                    return [
                        {tekst: this.translate.instant('TEKSTBESCHRIJVING.DE_VERANDERING_VAN')},
                        ...this.grootheidMetEntiteit(q as any)
                    ];
                }
            } else if (argType === 'derivative_zero') {
                return [{tekst: this.translate.instant('TEKSTBESCHRIJVING.NUL')}];
            }
            return [{tekst: this.dyna!.nodeName(argNode, true)}];
        };

        const getDSorteerNode = (argNode: any): any => {
            const argType = argNode.data('type');
            if (this.dyna!.isSubtypeOf(argType, 'd_calc')) {
                const res = this.dyna!.getRelationArguments(argNode);
                return res ? (getDSorteerNode(res.from) || getDSorteerNode(res.to)) : null;
            }
            if (argType === 'derivative') {
                const q = this.dyna!.inElements(argNode, '[type="quantity"]').first();
                return q.empty() ? null : q;
            }
            return null;
        };

        this.dyna!.typeNodes('d_calc').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;
            if (inOngelijkheidIds.has(node.id())) return;

            const result = this.dyna!.getRelationArguments(node as any);
            if (result) {
                const {from, to} = result;
                const type = node.data('type');
                let operator;
                switch (type) {
                    case 'd_calc_plus':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.PLUS');
                        break;
                    case 'd_calc_min':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.MIN');
                        break;
                    case 'd_calc_mult':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.KEER');
                        break;
                    case 'd_calc_div':
                        operator = this.translate.instant('TEKSTBESCHRIJVING.GEDEELD_DOOR');
                        break;
                    default:
                        operator = '?';
                }
                const regel: BeschrijvingFragment[] = [
                    ...vertaalDArgument(from),
                    {tekst: ` ${operator} `},
                    ...vertaalDArgument(to)
                ];
                regels.push({
                    regel: this.zin(regel),
                    status: 'nieuw',
                    groep: 'calculus',
                    nodes: [node.id(), from.id(), to.id()],
                    sorteerNode: getDSorteerNode(from) || getDSorteerNode(to)
                });
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de ongelijkheden tussen afgeleiden.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfAfgeleideOngelijkheid(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        this.dyna!.typeNodes('d_ineq').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;

            const type = node.data('type');
            const result = this.dyna!.getRelationArguments(node as any);
            if (result) {
                const {from, to} = result;

                // Sla de regel over als beide kanten 'derivative_zero' zijn
                if (from.data('type') === 'derivative_zero' && to.data('type') === 'derivative_zero') {
                    return;
                }

                const vertaalArgument = (argNode: any): BeschrijvingFragment[] => {
                    const argType = argNode.data('type');
                    // Als het een d_calc is, dan roepen we vertaalArgument recursief aan op de argumenten van de calc
                    if (this.dyna!.isSubtypeOf(argType, 'd_calc')) {
                        const res = this.dyna!.getRelationArguments(argNode);
                        if (res) {
                            let operator;
                            switch (argType) {
                                case 'd_calc_plus':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.PLUS');
                                    break;
                                case 'd_calc_min':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.MIN');
                                    break;
                                case 'd_calc_mult':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.KEER');
                                    break;
                                case 'd_calc_div':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.GEDEELD_DOOR');
                                    break;
                                default:
                                    operator = '?';
                            }
                            return [
                                ...vertaalArgument(res.from),
                                {tekst: ` ${operator} `},
                                ...vertaalArgument(res.to)
                            ];
                        }
                    }

                    if (argType === 'derivative') {
                        const q = this.dyna!.inElements(argNode, '[type="quantity"]').first();
                        if (!q.empty()) {
                            return [
                                {tekst: this.translate.instant('TEKSTBESCHRIJVING.DE_VERANDERING_VAN')},
                                ...this.grootheidMetEntiteit(q as any)
                            ];
                        }
                    } else if (argType === 'derivative_zero') {
                        return [{tekst: this.translate.instant('TEKSTBESCHRIJVING.NUL')}];
                    }
                    return [{tekst: this.dyna!.nodeName(argNode, true)}];
                };

                // Voor sortering gebruiken we de quantity node
                const getSorteerNode = (argNode: any): any => {
                    const argType = argNode.data('type');
                    if (this.dyna!.isSubtypeOf(argType, 'd_calc')) {
                        const res = this.dyna!.getRelationArguments(argNode);
                        return res ? (getSorteerNode(res.from) || getSorteerNode(res.to)) : null;
                    }
                    if (argType === 'derivative') {
                        const q = this.dyna!.inElements(argNode, '[type="quantity"]').first();
                        return q.empty() ? null : q;
                    }
                    return null;
                };

                const arg1 = vertaalArgument(from);
                const arg2 = vertaalArgument(to);
                const s1 = getSorteerNode(from);
                const s2 = getSorteerNode(to);

                let vergelijking;
                switch (type) {
                    case 'd_ineq_lt':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_KLEINER_DAN');
                        break;
                    case 'd_ineq_lte':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_KLEINER_DAN_OF_GELIJK_AAN');
                        break;
                    case 'd_ineq_eq':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_GELIJK_AAN');
                        break;
                    case 'd_ineq_gte':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_GROTER_DAN_OF_GELIJK_AAN');
                        break;
                    case 'd_ineq_gt':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_GROTER_DAN');
                        break;
                    default:
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.STAAT_IN_VERHOUDING_TOT');
                }

                const regel: BeschrijvingFragment[] = [
                    ...arg1,
                    {tekst: ` ${vergelijking} `},
                    ...arg2
                ];

                regels.push({
                    regel: this.zin(regel),
                    status: 'nieuw',
                    groep: 'afgeleide_ongelijkheid',
                    nodes: [node.id(), from.id(), to.id()],
                    sorteerNode: s1 || s2
                });
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de ongelijkheden tussen grootheden en hun waarden.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfGrootheidOngelijkheid(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const regels: BeschrijvingRegel[] = [];

        this.dyna!.typeNodes('q_ineq').forEach((node) => {
            if (!this.dyna!.match(node, condtag || null, condstate)) return;

            const type = node.data('type');
            const result = this.dyna!.getRelationArguments(node as any);
            this.log.debug(`q-ineq`, node?.data(), result?.from?.data(), result?.to?.data());
            if (result) {
                const {from, to} = result;

                // Het from-argument als quantity node, voor vergelijking bij quantity_space_point
                const fromQuantity = from.data('type') === 'quantity' ? from : null;

                const vertaalArgument = (argNode: any): BeschrijvingFragment[] => {
                    const argType = argNode.data('type');
                    // Als het een q_calc is, dan roepen we vertaalArgument recursief aan op de argumenten van de calc
                    if (this.dyna!.isSubtypeOf(argType, 'q_calc')) {
                        const res = this.dyna!.getRelationArguments(argNode);
                        if (res) {
                            let operator;
                            switch (argType) {
                                case 'q_calc_plus':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.PLUS');
                                    break;
                                case 'q_calc_min':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.MIN');
                                    break;
                                case 'q_calc_mult':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.KEER');
                                    break;
                                case 'q_calc_div':
                                    operator = this.translate.instant('TEKSTBESCHRIJVING.GEDEELD_DOOR');
                                    break;
                                default:
                                    operator = '?';
                            }
                            return [
                                ...vertaalArgument(res.from),
                                {tekst: ` ${operator} `},
                                ...vertaalArgument(res.to)
                            ];
                        }
                    }

                    if (argType === 'quantity') {
                        return this.grootheidMetEntiteit(argNode);
                    } else if (argType === 'quantity_space_point') {
                        const q = this.dyna!.getQuantityForQSValue(argNode);
                        const w = this.dyna!.nodeName(argNode, true);
                        // Als het waardebereik-element hoort bij dezelfde grootheid als het from-argument,
                        // dan volstaat alleen de naam (bijv. "min" i.p.v. "min van gewicht van koe")
                        if (q && fromQuantity && q.id() === fromQuantity.id()) {
                            return [{tekst: w}];
                        }
                        if (q) {
                            return [
                                {tekst: w},
                                {tekst: this.translate.instant('TEKSTBESCHRIJVING.VAN')},
                                ...this.grootheidMetEntiteit(q)
                            ];
                        }
                        return [{tekst: w}];
                    }
                    return [{tekst: this.dyna!.nodeName(argNode, true)}];
                };

                // Voor sortering gebruiken we de quantity node
                const getSorteerNode = (argNode: any): any => {
                    const argType = argNode.data('type');
                    if (this.dyna!.isSubtypeOf(argType, 'q_calc')) {
                        const res = this.dyna!.getRelationArguments(argNode);
                        return res ? (getSorteerNode(res.from) || getSorteerNode(res.to)) : null;
                    }
                    if (argType === 'quantity') return argNode;
                    if (argType === 'quantity_space_point') return this.dyna!.getQuantityForQSValue(argNode);
                    return null;
                };

                const arg1 = vertaalArgument(from);
                const arg2 = vertaalArgument(to);
                const s1 = getSorteerNode(from);
                const s2 = getSorteerNode(to);

                let vergelijking;
                switch (type) {
                    case 'q_ineq_lt':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_KLEINER_DAN');
                        break;
                    case 'q_ineq_lte':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_KLEINER_DAN_OF_GELIJK_AAN');
                        break;
                    case 'q_ineq_eq':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_GELIJK_AAN');
                        break;
                    case 'q_ineq_gte':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_GROTER_DAN_OF_GELIJK_AAN');
                        break;
                    case 'q_ineq_gt':
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.IS_GROTER_DAN');
                        break;
                    default:
                        vergelijking = this.translate.instant('TEKSTBESCHRIJVING.STAAT_IN_VERHOUDING_TOT');
                }

                // Altijd volledige zin: "Grootheid van Entiteit is bij aanvang gelijk aan Grootheid van Entiteit"
                const regel: BeschrijvingFragment[] = [
                    ...arg1,
                    {tekst: ` ${vergelijking} `},
                    ...arg2
                ];

                regels.push({
                    regel: this.zin(regel),
                    status: 'nieuw',
                    groep: 'grootheid_ongelijkheid',
                    nodes: [node.id(), from.id(), to.id()],
                    sorteerNode: s1 || s2
                });
            }
        });

        return this.sorteerRegelsOpLocatie(regels);
    }

    /**
     * Beschrijft de invloeden en proportionaliteiten in de volgorde van oorzaak naar gevolg.
     * Regels voor de volgorde:
     * 1. Grootheden die een proces zijn (exogeen of uitgaande invloed) gaan voor.
     *    Daarna volgen grootheden met een uitgaande proportionaliteit.
     * 2. Een grootheid die meer naar links staat (kleinere x-waarde) gaat voor.
     * 3. Vanaf een grootheid heeft het beschrijven van een doorgaande keten voorrang op de selectie
     *    van een nieuwe grootheid. Een keten bestaat uit proportionaliteiten en stopt bij een proces.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfOorzaakGevolg(condtag: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const finaleRegels: BeschrijvingRegel[] = [];

        // Haal alle relevante relaties op (influences en proportionalities)
        const influences = this.dyna!.typeNodes('influence').filter(n => this.dyna!.match(n, condtag || null, condstate));
        const proportionalities = this.dyna!.typeNodes('proportionality').filter(n => this.dyna!.match(n, condtag || null, condstate));

        const beschrevenRelatieIds = new Set<string>();

        // Bouw een map van uitgaande relaties per bron-grootheid
        const relatiesPerBron = new Map<string, { node: NodeSingular, type: 'influence' | 'proportionality', toId: string }[]>();
        const alleBronNodeIds = new Set<string>();

        const registreerRelatie = (relNode: NodeSingular, type: 'influence' | 'proportionality') => {
            const result = this.dyna!.getRelationArguments(relNode as any);
            if (result) {
                const {from, to} = result;
                const fromId = from.id();
                const toId = to.id();
                if (!relatiesPerBron.has(fromId)) relatiesPerBron.set(fromId, []);
                relatiesPerBron.get(fromId)!.push({node: relNode, type, toId});
                alleBronNodeIds.add(fromId);
            }
        };

        influences.forEach(n => registreerRelatie(n as any, 'influence'));
        proportionalities.forEach(n => registreerRelatie(n as any, 'proportionality'));

        if (alleBronNodeIds.size === 0) return finaleRegels;

        /**
         * Controleert of een grootheid een 'proces' is (heeft een exogeen of een uitgaande invloed).
         */
        const isProces = (nodeId: string): boolean => {
            const node = this.dyna!.getNode(nodeId);
            if (!node) return false;
            // Heeft het een exogeen?
            const exo = this.dyna!.getExoNode(node);
            if (exo && this.dyna!.match(exo, condtag || null, condstate)) return true;
            // Heeft het een uitgaande invloed?
            return (relatiesPerBron.get(nodeId) || []).some(r => r.type === 'influence');
        };

/*
        // Log voor elke bron of het een proces is
        for (const nodeId of alleBronNodeIds) {
            const label = this.dyna!.nodeName(this.dyna!.getNode(nodeId)!);
            this.log.debug(`Grootheid ${label} (ID: ${nodeId}) is een proces: ${isProces(nodeId)}`);
        }
*/

/*        // Sorteer op X-positie (links naar rechts)
        const nodeIdsArray = Array.from(alleBronNodeIds);
        const linksNaarRechts = [...nodeIdsArray].sort((idA, idB) => {
            const posA = this.dyna!.getNode(idA)!.position();
            const posB = this.dyna!.getNode(idB)!.position();
            return (Math.abs(posA.x - posB.x) > 5) ? posA.x - posB.x : posA.y - posB.y;
        });
        const labelsLinksNaarRechts = linksNaarRechts.map(id => this.dyna!.nodeName(this.dyna!.getNode(id)!)).join(', ');
        this.log.debug(`Grootheden gesorteerd van links naar rechts: ${labelsLinksNaarRechts}`);*/

        /**
         * Sorteert een lijst van node-ids volgens de prioriteitsregels:
         * 1. Processen eerst, dan de rest (die dus een uitgaande proportionaliteit moeten hebben).
         * 2. Meest linkse positie eerst.
         */
        const sorteerStartNodes = (nodeIds: string[]): string[] => {
            return [...nodeIds].sort((idA, idB) => {
                const procesA = isProces(idA);
                const procesB = isProces(idB);
                if (procesA !== procesB) return procesA ? -1 : 1;

                const nodeA = this.dyna!.getNode(idA)!;
                const nodeB = this.dyna!.getNode(idB)!;
                const posA = nodeA.position();
                const posB = nodeB.position();
                // Gebruik een kleine marge voor de x-positie (zoals in sorteerOpLocatie)
                if (Math.abs(posA.x - posB.x) > 5) {
                    return posA.x - posB.x;
                }
                return posA.y - posB.y;
            });
        };

/*        const uiteindelijkeSortering = sorteerStartNodes(nodeIdsArray);
        const labelsUiteindelijk = uiteindelijkeSortering.map(id => this.dyna!.nodeName(this.dyna!.getNode(id)!)).join(', ');
        this.log.debug(`Grootheden gesorteerd volgens de uiteindelijke sortering: ${labelsUiteindelijk}`);*/

        /**
         * Beschrijft een groep van relaties vanaf dezelfde bron-node.
         */
        const beschrijfRelatieGroep = (fromId: string, relaties: { node: NodeSingular, type: string, toId: string }[]): BeschrijvingRegel => {
            const fromNode = this.dyna!.getNode(fromId)!;
            const type = relaties[0].type;
            const fragmenten: BeschrijvingFragment[] = [];
            const alleNodes = new Set<string>([fromId]);
            relaties.forEach(r => {
                alleNodes.add(r.node.id());
                alleNodes.add(r.toId);
            });

            if (type === 'influence') {
                const q1Naam = this.dyna!.nodeName(fromNode);
                const ent1 = this.dyna!.getParent(fromNode);
                const ent1Naam = ent1 ? this.dyna!.nodeName(ent1) : '?';

                fragmenten.push({tekst: q1Naam, isGrootheid: true});

                const entiteitenPerGrootheid = new Map<string, string[]>();
                for (const r of relaties) {
                    const toNode = this.dyna!.getNode(r.toId)!;
                    const naam = this.dyna!.nodeName(toNode);
                    const entiteit = this.dyna!.getParent(toNode);
                    const entNaam = entiteit ? this.dyna!.nodeName(entiteit) : '?';
                    if (!entiteitenPerGrootheid.has(naam)) entiteitenPerGrootheid.set(naam, []);
                    if (!entiteitenPerGrootheid.get(naam)!.includes(entNaam)) entiteitenPerGrootheid.get(naam)!.push(entNaam);
                }

                const meervoudInfluence = Array.from(entiteitenPerGrootheid.keys()).length > 1;
                const influenceSleutel = meervoudInfluence ? 'TEKSTBESCHRIJVING.IS_EEN_PROCES_VAN_MEERVOUD' : 'TEKSTBESCHRIJVING.IS_EEN_PROCES_VAN';
                fragmenten.push({tekst: this.translate.instant(influenceSleutel, {entiteit: ent1Naam})});

                const groothedenPerEntiteitSet = new Map<string, { entiteitNamen: string[], grootheidNamen: string[] }>();
                for (const [gNaam, entNamen] of entiteitenPerGrootheid.entries()) {
                    const sleutel = [...entNamen].sort().join('|');
                    if (!groothedenPerEntiteitSet.has(sleutel)) {
                        groothedenPerEntiteitSet.set(sleutel, {entiteitNamen: entNamen, grootheidNamen: [gNaam]});
                    } else {
                        groothedenPerEntiteitSet.get(sleutel)!.grootheidNamen.push(gNaam);
                    }
                }

                const doelSets: BeschrijvingFragment[][] = [];
                for (const entry of groothedenPerEntiteitSet.values()) {
                    const gSets = entry.grootheidNamen.map(g => [{tekst: g, isGrootheid: true}]);
                    const eSets = entry.entiteitNamen.map(e => [{tekst: e}]);
                    doelSets.push([
                        ...this.voegFragmentenSamen(gSets),
                        // {tekst: ' ' + this.translate.instant('TEKSTBESCHRIJVING.VAN') + ' '},
                        {tekst: ' (' },
                        ...this.voegFragmentenSamen(eSets),
                        {tekst: ')'}
                    ]);
                }
                fragmenten.push(...this.voegFragmentenSamen(doelSets));
            } else {
                const als = this.translate.instant('TEKSTBESCHRIJVING.ALS');
                fragmenten.push({tekst: als}, ...this.grootheidMetEntiteit(fromNode));

                const entiteitenPerGrootheid = new Map<string, string[]>();
                const uniekeGrootheidnamen = new Set<string>();
                for (const r of relaties) {
                    const toNode = this.dyna!.getNode(r.toId)!;
                    const naam = this.dyna!.nodeName(toNode);
                    const entiteit = this.dyna!.getParent(toNode);
                    const entNaam = entiteit ? this.dyna!.nodeName(entiteit) : '?';
                    uniekeGrootheidnamen.add(naam);
                    if (!entiteitenPerGrootheid.has(naam)) entiteitenPerGrootheid.set(naam, []);
                    if (!entiteitenPerGrootheid.get(naam)!.includes(entNaam)) entiteitenPerGrootheid.get(naam)!.push(entNaam);
                }

                const groothedenPerEntiteitSet = new Map<string, { entiteitNamen: string[], grootheidNamen: string[] }>();
                for (const [gNaam, entNamen] of entiteitenPerGrootheid.entries()) {
                    const sleutel = [...entNamen].sort().join('|');
                    if (!groothedenPerEntiteitSet.has(sleutel)) {
                        groothedenPerEntiteitSet.set(sleutel, {entiteitNamen: entNamen, grootheidNamen: [gNaam]});
                    } else {
                        groothedenPerEntiteitSet.get(sleutel)!.grootheidNamen.push(gNaam);
                    }
                }

                const [voorG2, naG2] = this.translate.instant(uniekeGrootheidnamen.size > 1 ? 'TEKSTBESCHRIJVING.VERANDERT_DAN_VERANDEREN' : 'TEKSTBESCHRIJVING.VERANDERT_DAN_VERANDERT', {grootheid2: '[[G2]]'}).split('[[G2]]');
                fragmenten.push({tekst: voorG2});

                const doelSets: BeschrijvingFragment[][] = [];
                for (const entry of groothedenPerEntiteitSet.values()) {
                    const gSets = entry.grootheidNamen.map(g => [{tekst: g, isGrootheid: true}]);
                    const eSets = entry.entiteitNamen.map(e => [{tekst: e}]);
                    doelSets.push([
                        ...this.voegFragmentenSamen(gSets),
                        // {tekst: ' ' + this.translate.instant('TEKSTBESCHRIJVING.VAN') + ' '},
                        {tekst: ' ('},
                        ...this.voegFragmentenSamen(eSets),
                        {tekst: ')'}
                    ]);
                }
                fragmenten.push(...this.voegFragmentenSamen(doelSets));
                if (naG2) fragmenten.push({tekst: naG2});
            }

            return {
                regel: this.zin(fragmenten),
                status: 'nieuw',
                groep: 'oorzaak_gevolg',
                nodes: Array.from(alleNodes),
                sorteerNode: fromNode
            };
        };

        /**
         * Volgt een pad vanaf een grootheid. Beschrijft eerst influences, dan proportionaliteiten.
         * Voor influences en proportionaliteiten wordt de keten vervolgd tot een proces wordt bereikt.
         */
        const volgPad = (fromId: string) => {
            const rels = relatiesPerBron.get(fromId) || [];
            const onbeschrevenInfluences = rels.filter(r => r.type === 'influence' && !beschrevenRelatieIds.has(r.node.id()));
            const onbeschrevenProportionalities = rels.filter(r => r.type === 'proportionality' && !beschrevenRelatieIds.has(r.node.id()));

            // De doelen die we moeten volgen in de keten
            const doelenTeVolgen = new Set<string>();

            // Beschrijf influences vanaf deze node (indien nog niet beschreven)
            if (onbeschrevenInfluences.length > 0) {
                onbeschrevenInfluences.forEach(r => {
                    beschrevenRelatieIds.add(r.node.id());
                    doelenTeVolgen.add(r.toId);
                });
                finaleRegels.push(beschrijfRelatieGroep(fromId, onbeschrevenInfluences));
            }

            // Beschrijf proportionaliteiten vanaf deze node
            if (onbeschrevenProportionalities.length > 0) {
                onbeschrevenProportionalities.forEach(r => {
                    beschrevenRelatieIds.add(r.node.id());
                    doelenTeVolgen.add(r.toId);
                });
                finaleRegels.push(beschrijfRelatieGroep(fromId, onbeschrevenProportionalities));
            }

            // Volg de keten voor de doelen (influences en proportionaliteiten)
            // Sorteer de doelen eerst (links naar rechts)
            const doelen = sorteerStartNodes(Array.from(doelenTeVolgen));
            for (const toId of doelen) {
                // Stop de keten als het doel een proces is (volgens regel 3)
                if (!isProces(toId)) {
                    volgPad(toId);
                }
            }
        };

        // Hoofdlus: start bij de onbeschreven bron-nodes in de juiste volgorde
        let nogTeVerwerken = sorteerStartNodes(Array.from(alleBronNodeIds));
        while (nogTeVerwerken.length > 0) {
            const startId = nogTeVerwerken[0];
            volgPad(startId);

            // Update de lijst van nog te verwerken bronnen (verwijder die waarvan alle uitgaande relaties beschreven zijn)
            nogTeVerwerken = sorteerStartNodes(Array.from(alleBronNodeIds).filter(id => {
                const rels = relatiesPerBron.get(id) || [];
                return rels.some(r => !beschrevenRelatieIds.has(r.node.id()));
            }));
        }

        return finaleRegels;
    }

    /**
     * Beschrijft de waardebereiken van de grootheden in het model.
     * @param condtag De naam van de conditieset (optioneel).
     * @param condstate De status binnen de conditieset (optioneel).
     * @private
     */
    private beschrijfWaardebereiken(condtag?: string | null, condstate?: 'cond' | 'cons' | null): BeschrijvingRegel[] {
        const result: BeschrijvingRegel[] = [];
        const quantityFeiten = new Map<string, { feiten: BeschrijvingFragment[][], nodes: string[] }>();

        // 1. Verzamel alle feiten per grootheid
        this.dyna!.typeNodes('quantity').forEach((node) => {
            const feiten: BeschrijvingFragment[][] = [];
            const betrokkenNodes: string[] = [node.id()];

            // 1a. Het waardebereik zelf ("kan waarden X aannemen")
            const qs = this.dyna!.getQS(node);
            const waarden = qs ? this.dyna!.getQSValueNames(qs) : [];
            if (waarden.length > 0 && this.dyna!.match(node, condtag || null, condstate)) {
                const waardenTekst = this.formatteerLijst(waarden);
                const termKey = waarden.length === 1 ? 'TEKSTBESCHRIJVING.WAARDE' : 'TEKSTBESCHRIJVING.WAARDEN';
                const term = this.translate.instant(termKey);
                // Onthoud dat dit fragment afhankelijk is van of er meerdere grootheden zijn
                feiten.push([{
                    tekst: 'TEKSTBESCHRIJVING.KAN_AANNEMEN',
                    isKanAannemen: true,
                    params: { term, waarden: waardenTekst }
                }]);
            }

            // 1b. De beginwaarden ("heeft beginwaarde X")
            this.dyna!.getQValueNodes(node).forEach(qValueNode => {
                const inherit = condtag !== null;
                if (this.dyna!.match(qValueNode, condtag || null, condstate, inherit)) {
                    const qsElement = this.dyna!.inElements(qValueNode).filter(n => n.isNode()).first() as NodeSingular;
                    const beginwaarde = qsElement ? this.dyna!.nodeName(qsElement, true) : undefined;
                    if (beginwaarde) {
                        const beginFragment = this.translate.instant('TEKSTBESCHRIJVING.BEGINWAARDE', {waarde: beginwaarde});
                        feiten.push([{tekst: beginFragment}]);
                        betrokkenNodes.push(qValueNode.id());
                    }
                }
            });

            // 1c. De afgeleide waarden ("neemt toe", "neemt af" of "is constant")
            this.dyna!.getDValueNodes(node).forEach(dValueNode => {
                const inherit = condtag !== null;
                if (this.dyna!.match(dValueNode, condtag || null, condstate, inherit)) {
                    const dqsElement = this.dyna!.inElements(dValueNode).filter(n => n.isNode()).first() as NodeSingular;
                    const afgeleideType = dqsElement ? dqsElement.data('type') : 'onbekend';
                    feiten.push([{tekst: afgeleideType, isDType: true}]);
                    betrokkenNodes.push(dValueNode.id());
                }
            });

            // 1d. Het exogene type ("is altijd constant", "heeft sinusvorm", etc.)
            const exoNode = this.dyna!.getExoNode(node);
            if (exoNode) {
                const inherit = condtag !== null;
                if (this.dyna!.match(exoNode, condtag || null, condstate, inherit)) {
                    const exoType = exoNode.data('type');
                    const exoWaarde = exoType ? this.vertaalExoType(exoType) : undefined;
                    if (exoWaarde) {
                        feiten.push([{tekst: exoWaarde}]);
                        betrokkenNodes.push(exoNode.id());
                    }
                }
            }

            if (feiten.length > 0) {
                quantityFeiten.set(node.id(), {feiten, nodes: betrokkenNodes});
            }
        });

        // 2. Groepeer grootheden met identieke feiten-sets
        const groepen = new Map<string, {
            nodes: NodeSingular[],
            feiten: BeschrijvingFragment[][],
            betrokkenNodes: string[]
        }>();
        quantityFeiten.forEach((data, qId) => {
            const node = this.dyna!.getNode(qId);
            if (!node) return;
            // Gebruik een stabiele sleutel voor de feiten-set om groepering te garanderen
            const sleutel = data.feiten.map(set => set.map(f => {
                const paramsSleutel = f.params ? `|${JSON.stringify(f.params)}` : '';
                return `${f.tekst}${paramsSleutel}`;
            }).join(' ')).join('|');
            if (!groepen.has(sleutel)) {
                groepen.set(sleutel, {nodes: [], feiten: data.feiten, betrokkenNodes: []});
            }
            const groep = groepen.get(sleutel)!;
            groep.nodes.push(node);
            groep.betrokkenNodes.push(...data.nodes);
        });

        // 3. Genereer finale zinnen
        groepen.forEach((groep) => {
            // Groepeer nodes per entiteit
            const perEntiteit = new Map<string, {
                entiteit: NodeSingular,
                quantityNamen: string[],
                qNodes: NodeSingular[]
            }>();
            groep.nodes.forEach(node => {
                const entiteit = this.dyna!.getParent(node);
                const entiteitId = entiteit?.id() || 'geen_entiteit';
                if (!perEntiteit.has(entiteitId)) {
                    perEntiteit.set(entiteitId, {
                        entiteit: entiteit!,
                        quantityNamen: [],
                        qNodes: []
                    });
                }
                const data = perEntiteit.get(entiteitId)!;
                const naam = this.dyna!.nodeName(node);
                if (!data.quantityNamen.includes(naam)) {
                    data.quantityNamen.push(naam);
                }
                data.qNodes.push(node);
            });

            // Groepeer entiteiten die dezelfde set grootheidnamen hebben
            const entiteitenPerGrootheidSet = new Map<string, {
                grootheidNamen: string[],
                entiteiten: NodeSingular[],
                qNodes: NodeSingular[]
            }>();
            perEntiteit.forEach(data => {
                const sleutel = JSON.stringify(data.quantityNamen.sort());
                if (!entiteitenPerGrootheidSet.has(sleutel)) {
                    entiteitenPerGrootheidSet.set(sleutel, {
                        grootheidNamen: data.quantityNamen,
                        entiteiten: [],
                        qNodes: []
                    });
                }
                const set = entiteitenPerGrootheidSet.get(sleutel)!;
                set.entiteiten.push(data.entiteit);
                set.qNodes.push(...data.qNodes);
            });

            // Bouw de samengevoegde onderwerpen
            const onderwerpen: BeschrijvingFragment[][] = [];
            entiteitenPerGrootheidSet.forEach(set => {
                const grootheidSets: BeschrijvingFragment[][] = set.grootheidNamen.map(naam => [{
                    tekst: naam,
                    isGrootheid: true
                }]);
                const grootheidFragmenten = this.voegFragmentenSamen(grootheidSets);

                if (set.entiteiten.length > 0) {
                   // const van = this.translate.instant('TEKSTBESCHRIJVING.VAN');
                    const entiteitSets: BeschrijvingFragment[][] = set.entiteiten.map(e => [{tekst: this.dyna!.nodeName(e)}]);
                    const entiteitFragmenten = this.voegFragmentenSamen(entiteitSets);
                    // grootheidFragmenten.push({tekst: ` ${van} `}, ...entiteitFragmenten);
                    grootheidFragmenten.push({tekst: ` (`}, ...entiteitFragmenten, {tekst: ')'});
                }
                onderwerpen.push(grootheidFragmenten);
            });

            const finaleOnderwerp = this.voegFragmentenSamen(onderwerpen);

            const meervoud = groep.nodes.length > 1;
            const feitenMetMeervoud = groep.feiten.map(fSet => {
                return fSet.map(f => {
                    if (f.isDType) {
                        return {tekst: this.vertaalDType(f.tekst, meervoud)};
                    }
                    if (f.isKanAannemen) {
                        const sleutel = meervoud ? 'TEKSTBESCHRIJVING.KAN_AANNEMEN_MEERVOUD' : 'TEKSTBESCHRIJVING.KAN_AANNEMEN';
                        return {tekst: this.translate.instant(sleutel, f.params)};
                    }
                    return f;
                });
            });

            const zinsdelen: BeschrijvingFragment[][] = [finaleOnderwerp, ...feitenMetMeervoud];

            result.push({
                regel: this.zin(this.voegFragmentenSamen(zinsdelen, ' ')),
                status: 'nieuw',
                groep: 'waarden',
                nodes: groep.betrokkenNodes,
                sorteerNode: groep.nodes[0]
            });
        });

        return this.sorteerOpEntiteitEnGrootheid(result);
    }

    /**
     * Highlight de nodes die bij de gehoverde regel horen, via de selectie-service.
     * @param nodes De lijst met node-ids.
     * @protected
     */
    protected onMouseIn(nodes: string[]): void {
        this.feedbackSelectie.highlightNodes(nodes);
    }

    /**
     * Verwijdert de highlights weer.
     * @protected
     */
    protected onMouseOut(): void {
        this.feedbackSelectie.wisSelectie();
    }

    /**
     * Sorteert regels op basis van de schermlocatie van de entiteit en de grootheid.
     * @param regels De lijst met beschrijvingsregels.
     * @returns De gesorteerde lijst.
     * @private
     */
    private sorteerOpEntiteitEnGrootheid(regels: BeschrijvingRegel[]): BeschrijvingRegel[] {
        return regels.sort((a, b) => {
            const nodeA = a.sorteerNode;
            const nodeB = b.sorteerNode;
            if (!nodeA || !nodeB) return 0;

            const entiteitA = this.dyna!.getParent(nodeA);
            const entiteitB = this.dyna!.getParent(nodeB);

            // 1. Sorteer op schermlocatie van de entiteit
            if (entiteitA && entiteitB && entiteitA.id() !== entiteitB.id()) {
                const posEntA = entiteitA.position();
                const posEntB = entiteitB.position();

                // Bij vergelijken van positie beschouwen we y als gelijk wanneer deze minder dan 100px afwijken
                if (Math.abs(posEntA.y - posEntB.y) >= this.SORTEER_TOLERANTIE_Y) {
                    return posEntA.y - posEntB.y;
                }
                // Als y gelijk is (binnen tolerantie), kijken we naar x (ook met tolerantie)
                if (Math.abs(posEntA.x - posEntB.x) >= this.SORTEER_TOLERANTIE_X) {
                    return posEntA.x - posEntB.x;
                }
            } else if (entiteitA && !entiteitB) {
                return -1;
            } else if (!entiteitA && entiteitB) {
                return 1;
            }

            // 2. Sorteer op schermlocatie van de grootheid zelf: alleen x-positie, geen y
            const posA = nodeA.position();
            const posB = nodeB.position();
            return posA.x - posB.x;
        });
    }

    /**
     * Sorteert een collectie van nodes op hun positie op het scherm.
     * Van linksboven naar rechtsonder met tolerantie.
     * @param nodes De nodes die gesorteerd moeten worden.
     * @returns De gesorteerde lijst met nodes.
     * @private
     */
    private sorteerOpLocatie(nodes: any): any[] {
        const compareFn = (a: any, b: any) => {
            const posA = a.position ? a.position() : {x: 0, y: 0};
            const posB = b.position ? b.position() : {x: 0, y: 0};

            this.log.debug(`Sorteer op locatie`, this.dyna!.nodeName(a), posA.y, this.dyna!.nodeName(b), posB.y, Math.abs(posA.y - posB.y) >= this.SORTEER_TOLERANTIE_Y ? "ongelijke y" : "gelijke y");
            if (Math.abs(posA.y - posB.y) >= this.SORTEER_TOLERANTIE_Y) {
                return posA.y - posB.y;
            }
            return posA.x - posB.x;
        };

        if (!nodes || typeof nodes.toArray !== 'function') {
            return Array.isArray(nodes) ? [...nodes].sort(compareFn) : [];
        }
        return nodes.toArray().sort(compareFn);
    }

    /**
     * Sorteert beschrijvingsregels op basis van de schermlocatie van hun sorteerNode.
     * Gebruikt de tolerantie-regels.
     * @param regels De lijst met beschrijvingsregels.
     * @returns De gesorteerde lijst.
     * @private
     */
    private sorteerRegelsOpLocatie(regels: BeschrijvingRegel[]): BeschrijvingRegel[] {
        return regels.sort((a, b) => {
            if (!a.sorteerNode || !b.sorteerNode) return 0;
            const posA = a.sorteerNode.position();
            const posB = b.sorteerNode.position();

            if (Math.abs(posA.y - posB.y) >= this.SORTEER_TOLERANTIE_Y) {
                return posA.y - posB.y;
            }
            return posA.x - posB.x;
        });
    }

    /**
     * Vertaalt een derivative_element type naar een gelokaliseerde tekst.
     * @param type Het type (bijv. derivative_plus).
     * @param meervoud Of de meervoudsvorm gebruikt moet worden.
     * @private
     */
    private vertaalDType(type: string, meervoud: boolean = false): string {
        switch (type) {
            case 'derivative_plus':
                return meervoud ? this.translate.instant('TEKSTBESCHRIJVING.NEEM_TOE_MEERVOUD') : this.translate.instant('TEKSTBESCHRIJVING.NEEMT_TOE');
            case 'derivative_zero':
                return meervoud ? this.translate.instant('TEKSTBESCHRIJVING.BLIJVEN_GELIJK_MEERVOUD') : this.translate.instant('TEKSTBESCHRIJVING.IS_CONSTANT');
            case 'derivative_min':
                return meervoud ? this.translate.instant('TEKSTBESCHRIJVING.NEEM_AF_MEERVOUD') : this.translate.instant('TEKSTBESCHRIJVING.NEEMT_AF');
            default:
                return type;
        }
    }

    /**
     * Vertaalt een q_exo type naar een Nederlandse tekst.
     * @param type Het type (bijv. q_exo_steady).
     * @private
     */
    private vertaalExoType(type: string): string {
        switch (type) {
            case 'q_exo_decr':
                return this.translate.instant('TEKSTBESCHRIJVING.NEEMT_ALTIJD_AF');
            case 'q_exo_steady':
                return this.translate.instant('TEKSTBESCHRIJVING.IS_ALTIJD_CONSTANT');
            case 'q_exo_incr':
                return this.translate.instant('TEKSTBESCHRIJVING.NEEMT_ALTIJD_TOE');
            case 'q_exo_sin':
                return this.translate.instant('TEKSTBESCHRIJVING.HEEFT_SINUSVORM');
            case 'q_exo_para_pos':
                return this.translate.instant('TEKSTBESCHRIJVING.HEEFT_BERGPABABOOLVORM');
            case 'q_exo_para_neg':
                return this.translate.instant('TEKSTBESCHRIJVING.HEEFT_DALPABABOOLVORM');
            case 'q_exo_random':
                return this.translate.instant('TEKSTBESCHRIJVING.VERANDERT_WILLEKEURIG');
            default:
                return type;
        }
    }

    /**
     * Geeft de naam van de grootheid en de naam van zijn entiteit (parent).
     * @param node Het ID van de node of de node-instantie zelf.
     * @returns De geformatteerde fragmenten: "<grootheid> van <entiteit>".
     * @private
     */
    private grootheidMetEntiteit(node: any): BeschrijvingFragment[] {
        const naam = this.dyna!.nodeName(node);
        const parent = this.dyna!.getParent(node);
        if (!parent) return [{tekst: naam, isGrootheid: true}];
        return [
            {tekst: naam, isGrootheid: true},
            //zonder de 'van' ertussen
            // {tekst: `${this.translate.instant('TEKSTBESCHRIJVING.VAN')}${this.dyna!.nodeName(parent)}`}
            {tekst: ` (${this.dyna!.nodeName(parent)})`}
        ];
    }

    /**
     * Combineert twee grootheden met hun entiteit(en) in een fragmentlijst.
     * @param first De eerste grootheid-node.
     * @param second De tweede grootheid-node.
     * @returns De gecombineerde fragmenten: "<grootheid1> en <grootheid2> van <entiteit>" of "<grootheid1> van <entiteit1> en <grootheid2> van <entiteit2>".
     * @private
     */
    private tweeGroothedenMetEntiteit(first: any, second: any): BeschrijvingFragment[] {
        const n1 = this.dyna!.nodeName(first);
        const n2 = this.dyna!.nodeName(second);
        const p1 = this.dyna!.getParent(first);
        const p2 = this.dyna!.getParent(second);
        const en1 = p1 ? this.dyna!.nodeName(p1) : '';
        const en = this.translate.instant('TEKSTBESCHRIJVING.EN');
        // const van = this.translate.instant('TEKSTBESCHRIJVING.VAN');

        if (p1 && p2 && p1.id() === p2.id()) {
            // Zelfde entiteit: <grootheid1> en <grootheid2> van <entiteit>
            return [
                {tekst: n1, isGrootheid: true},
                {tekst: en},
                {tekst: n2, isGrootheid: true},
                // {tekst: `${van}${en1}`}
                {tekst: ` (${en1})`}
            ];
        }

        // Verschillende entiteiten of geen entiteiten
        const fragmenten: BeschrijvingFragment[] = [];
        fragmenten.push(...this.grootheidMetEntiteit(first));
        fragmenten.push({tekst: en});
        fragmenten.push(...this.grootheidMetEntiteit(second));
        return fragmenten;
    }

}
